import { PatientId, WithPatientId } from '../surgicalEvent.slice';
import { doctorBffApi, InvalidateArr } from '@/modules';
import { MedsService } from './meds.service';
import {
  Medication,
  MedicationAtc,
  MedicationInterruptionAutomationProperty
} from './meds.model';
import { wrapError } from '../../error';

export type UpsertMedArg = {
  medication: Partial<Medication>;
  applyAutomation?: boolean;
};

export const medsApi = doctorBffApi.injectEndpoints({
  endpoints: (builder) => ({
    meds: builder.query<Medication[], PatientId>({
      queryFn: ({ patientId }) =>
        MedsService.getInstance(patientId).fetch().catch(wrapError),
      providesTags: (result, error, { patientId }) => [
        { type: 'Meds', id: patientId }
      ]
    }),

    medsAtcList: builder.query<MedicationAtc[], { name?: string } | void>({
      queryFn: (params) =>
        MedsService.fetchMedicationAtcList(params?.name).catch(wrapError)
    }),

    interruptionPropertiesByAtc: builder.query<
      MedicationInterruptionAutomationProperty[],
      { atc?: string }
    >({
      queryFn: (params) =>
        MedsService.fetchInterruptionAutomationProperties(params?.atc).catch(
          wrapError
        )
    }),

    dryRunMedsAutomation: builder.mutation<
      Medication,
      WithPatientId<{ medication: Partial<Medication> }>
    >({
      queryFn: ({ patientId, medication }) =>
        MedsService.getInstance(patientId)
          .dryRunAutomation(medication)
          .then(({ data }) => ({ data }))
          .catch(wrapError)
    }),

    upsertMed: builder.mutation<Medication[], WithPatientId<UpsertMedArg>>({
      queryFn: ({ patientId, medication: med, applyAutomation = true }) =>
        MedsService.getInstance(patientId)
          .upsert(med, { automation: applyAutomation })
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId, medication: med }) => {
        const arr: InvalidateArr = [
          { type: 'Meds', id: patientId },
          'Dashboard'
        ];
        if (!med.id) {
          // for ASA prediction update, only if we're adding a medication
          arr.push({ type: 'SurgeryInfo', id: patientId });

          // for meds automation relevant mho items, only if we're adding a medication
          arr.push({ type: 'MedicalHo', id: patientId });

          // for noKnownMedications chip update, only if we're adding a medication
          arr.push({ type: 'ActiveSurgicalEvent', id: patientId });
        }

        return arr;
      }
    }),

    deleteMed: builder.mutation<Medication[], WithPatientId<{ medId: string }>>(
      {
        queryFn: ({ patientId, medId }) =>
          MedsService.getInstance(patientId).delete(medId).catch(wrapError),
        invalidatesTags: (result, error, { patientId }) => [
          { type: 'Meds', id: patientId },
          { type: 'SurgeryInfo', id: patientId }, // for ASA prediction update
          'Dashboard'
        ]
      }
    ),

    deleteMeds: builder.mutation<
      Medication[],
      WithPatientId<{ ids: string[] }>
    >({
      queryFn: ({ patientId, ids }) =>
        MedsService.getInstance(patientId).deleteMany(ids).catch(wrapError),
      invalidatesTags: (result, error, { patientId }) => [
        { type: 'Meds', id: patientId },
        { type: 'SurgeryInfo', id: patientId },
        'Dashboard'
      ]
    }),

    resetMeds: builder.mutation<Medication[], PatientId>({
      queryFn: ({ patientId }) =>
        MedsService.getInstance(patientId).resetAll().catch(wrapError),
      invalidatesTags: (result, error, { patientId }) => [
        { type: 'Meds', id: patientId },
        { type: 'ActiveSurgicalEvent', id: patientId },
        { type: 'SurgeryInfo', id: patientId },
        'Dashboard'
      ]
    }),

    setNoKnownMedications: builder.mutation<
      void,
      WithPatientId<{ value: boolean }>
    >({
      queryFn: ({ patientId, value }) =>
        MedsService.getInstance(patientId)
          .setNoKnownMedications(value)
          .then(() => ({ data: {} as any }))
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId }) =>
        !error ? [{ type: 'ActiveSurgicalEvent', id: patientId }] : []
    })
  }),
  overrideExisting: false
});

export const {
  useMedsQuery,
  useUpsertMedMutation,
  useDryRunMedsAutomationMutation,
  useDeleteMedMutation,
  useDeleteMedsMutation,
  useResetMedsMutation,
  useSetNoKnownMedicationsMutation,
  useMedsAtcListQuery: useMedsAtcList,
  useInterruptionPropertiesByAtcQuery: useInterruptionPropertiesByAtc
} = medsApi;
